import type { Country } from 'src/api/zrm';
import { projectLonLat } from 'src/tv/live/nordicOutlines';

export interface MapPlace {
  name: string;
  x: number;
  y: number;
}

/** zrm `CallOffice`; also picks its `CityIcon`. */
export type OfficeId = 'stockholm' | 'uppsala' | 'oslo';

export interface Office extends MapPlace {
  id: OfficeId;
}

export interface Call {
  /** Telavox's call id. */
  id: string;
  direction: 'incoming' | 'outgoing' | null;
  office: Office;
  customer: MapPlace;
  /** The customer's, from the phone number. */
  country: Country;
  /** When the call started, by Telavox; shown in the list. */
  startedAt: number;
  /** When it reached the screen; the arc is animated from this, so the TV's clock does not matter. */
  receivedAt: number;
}

export const place = (name: string, lon: number, lat: number): MapPlace => {
  const [x, y] = projectLonLat(lon, lat);

  return { name, x, y };
};

const SWEDISH_TOWNS = [
  place('Kiruna', 20.23, 67.86),
  place('Luleå', 22.15, 65.58),
  place('Skellefteå', 20.95, 64.75),
  place('Umeå', 20.26, 63.83),
  place('Östersund', 14.64, 63.18),
  place('Sundsvall', 17.31, 62.39),
  place('Gävle', 17.14, 60.67),
  place('Falun', 15.63, 60.61),
  place('Karlstad', 13.5, 59.38),
  place('Västerås', 16.54, 59.61),
  place('Örebro', 15.21, 59.27),
  place('Norrköping', 16.19, 58.59),
  place('Linköping', 15.62, 58.41),
  place('Göteborg', 11.97, 57.71),
  place('Jönköping', 14.16, 57.78),
  place('Visby', 18.29, 57.64),
  place('Växjö', 14.81, 56.88),
  place('Kalmar', 16.36, 56.66),
  place('Halmstad', 12.86, 56.67),
  place('Helsingborg', 12.69, 56.05),
  place('Malmö', 13.0, 55.6),
  place('Haparanda', 24.14, 65.84),
];

const NORWEGIAN_TOWNS = [
  place('Tromsø', 18.96, 69.65),
  place('Bodø', 14.4, 67.28),
  place('Trondheim', 10.4, 63.43),
  place('Ålesund', 6.15, 62.47),
  place('Bergen', 5.32, 60.39),
  place('Stavanger', 5.73, 58.97),
  place('Kristiansand', 7.99, 58.15),
  place('Lillehammer', 10.47, 61.12),
  place('Hamar', 11.07, 60.79),
  place('Drammen', 10.2, 59.74),
  place('Fredrikstad', 10.93, 59.22),
];

/** Drawn as dots, without names, so the map reads as Sweden and Norway. */
export const TOWNS: MapPlace[] = [...SWEDISH_TOWNS, ...NORWEGIAN_TOWNS];

// Offices are told apart by where the arc starts and by their `CityIcon`, not by colour.
export const OFFICES: Record<OfficeId, Office> = {
  stockholm: { id: 'stockholm', ...place('Stockholm', 18.07, 59.33) },
  uppsala: { id: 'uppsala', ...place('Uppsala', 17.64, 59.86) },
  oslo: { id: 'oslo', ...place('Oslo', 10.75, 59.91) },
};
